/**
 * @module common/containers/app
 */
import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { scroller } from 'react-scroll';
import Tooltip from 'react-tooltip';
import * as Sentry from '@sentry/browser';
import { ADMIN_REGEX } from 'common/variables';
import Header from 'header/containers';
import Footer from 'common/components/base_ui/footer';
import { LeftSideNavigation, RightSideNavigation } from
  'common/components/base_ui/side_navigation';
import HelpDrawer from 'common/components/base_ui/help_drawer';
import RightDrawer from 'common/containers/right_drawer';
import { toggleRightDrawer, toggleRightSideNav, getHeaderAvatar } from 'header/ducks';
import { Drawer as NotificationsDrawer } from 'notifications';
import { Meta } from 'common/components/meta';
import { fetchCategoriesIfNeeded } from 'common/ducks/categories';
import Banner from 'common/containers/banner';
import GlobalModals from 'common/components/modal/globals';
import { handleIosModalsOverlap } from 'common/utils';
import Announcements from 'announcements/containers';

if (process.env.WEBPACK) require('assets/scss/main.scss');

/**
 * Main App component rendering the application
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */

class App extends Component {
  componentDidMount() {
    const { userProfile } = this.props;

    /* identify user in case an error is reported to Sentry */
    if (userProfile.id) {
      Sentry.setUser({
        email: userProfile.email,
        username: userProfile.username,
        id: userProfile.id
      });
    }

    handleIosModalsOverlap();

    window.addEventListener('scroll', this.hideTooltips.bind(this), true);
  }

  componentWillReceiveProps(nextProps) {
    const { location } = this.props;
    const hasPathChanged = location.pathname !== nextProps.location.pathname;
    const hasQueryChanged = !isEqual(location.query, nextProps.location.query);
    const keepScroll = nextProps.location.query.keep_scroll_on_url_change === "true";

    if ((hasPathChanged || hasQueryChanged) && !keepScroll) {
      scroller.scrollTo('app-header', { containerId: 'app-scroller' });
    }
  }

  hideTooltips() {
    Tooltip.hide();
  }

  render() {
    const {
      header, userProfile, userAvatar, unseenNotificationsCount,
      toggleRightNav, location, ...props
    } = this.props;
    if (location.pathname.match(ADMIN_REGEX)) {
      return (<div className={classNames('l-app', header.content, header.tabContent)}>
        {props.children}
      </div>);
    }

    return (<div
      className={classNames('l-app', {
        marketing: header.isMarketing
      }, header.content, header.tabContent)}
    >
      {/* The component below makes sure that the default helmconfig definitions are loaded
          for every route. Definitions passed to nested <Meta> components will override
          the parent ones. */}
      <Meta />

      <div className={classNames('side-drawer-container left', { visible: header.showLeftSideNav })}>
        <LeftSideNavigation links={header.topNavigation} />
      </div>
      <div
        className={classNames('side-drawer-container right right-nav', {
          visible: header.showRightSideNav
        })}
      >
        <RightSideNavigation
          links={header.topNavigation}
          {...{ userProfile, userAvatar, unseenNotificationsCount, toggleRightNav }}
        />
      </div>

      <div className={classNames('top-drawer-container', { visible: header.helpDrawerVisible })}>
        <HelpDrawer />
      </div>

      <NotificationsDrawer />

      <div
        id="app-scroller"
        className={
          classNames('l-site-wrap scrolled', {
            left: header.showLeftSideNav,
            down: header.helpDrawerVisible,
            right: header.showRightDrawer,
            expanded: header.isCollapsed
          })
        }
      >
        <Banner />

        <Announcements />

        {header.showRightDrawer &&
          <div className="drawer-overlay" role="button" onClick={props.toggleRightDrawer} />
        }

        {header.showRightSideNav &&
          <div className="drawer-overlay hide-for-large" role="button" onClick={toggleRightNav} />
        }

        { !header.isCollapsed && <Header header={header} /> }

        {props.children}

        <Footer />
      </div>

      <GlobalModals />

      <div
        className={
          classNames('row side-drawer-container right', { visible: header.showRightDrawer })
        }
      >
        <RightDrawer />
      </div>
    </div>);
  }
}

/**
 * PropTypes.
 *
 * @property {node} children - all the inner components
 * @property {object} header - header components state
 * @property {func} toggleRightDrawer - toggle right drawer
 * @property {func} toggleRightNav - toggle right side navigation
 * @property {str} userAvatar - user avatar
 * @property {number} unseenNotificationsCount - number of unseen notifications
 */
App.propTypes = {
  children: PropTypes.node,
  header: PropTypes.object,
  toggleRightDrawer: PropTypes.func,
  userAvatar: PropTypes.string,
  userProfile: PropTypes.object,
  unseenNotificationsCount: PropTypes.number,
  toggleRightNav: PropTypes.func,
  location: PropTypes.object,
};

App.need = [
  fetchCategoriesIfNeeded
];

function mapStateToProps(state) {
  return {
    header: state.header,
    userAvatar: getHeaderAvatar(state),
    userProfile: state.user.profile_data,
    unseenNotificationsCount: (state.notifications.data.results || []).filter(n => !n.seen).length
  };
}

export default withRouter(connect(mapStateToProps, {
  toggleRightDrawer,
  toggleRightNav: toggleRightSideNav
})(App));



// WEBPACK FOOTER //
// ./common/containers/app.jsx